import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {AutoCorrection} from './AutoCorrection.types'

interface CorrectionMessageParams {
  minValue: number
  maxValue: number
  step: number
  quantityUom: string
}

interface UseQuantityCorrectionResult {
  correctionMessage: string
  hasCorrection: boolean
  handleAutoCorrect: (correction: AutoCorrection) => void
}

/**
 * Custom hook for managing auto-correction state and messages.
 * Handles correction state, auto-clearing after 5 seconds, and message generation.
 * Used across OrderManagement and OrderIntake quantity steps to avoid code duplication.
 */
export const useQuantityCorrection = ({
  minValue,
  maxValue,
  step,
  quantityUom
}: CorrectionMessageParams): UseQuantityCorrectionResult => {
  const {t} = useTranslation()

  // State to track auto-corrections
  const [correction, setCorrection] = useState<AutoCorrection | null>(null)

  // Automatically clear correction message after 5 seconds
  React.useEffect(() => {
    if (correction) {
      const timer = setTimeout(() => {
        setCorrection(null)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [correction])

  // Handle auto-correction
  const handleAutoCorrect = (newCorrection: AutoCorrection) => {
    setCorrection(newCorrection)
  }

  // Generate correction message based on reason
  const correctionMessage = React.useMemo(() => {
    if (!correction) return ''

    const {originalValue, correctedValue, reason} = correction

    switch (reason) {
      case 'step':
        return t(
          'orderIntake.rmc.steps.shared.quantityCorrection.stepAdjustment',
          'Your entered value ({{originalValue}}) was adjusted to {{correctedValue}} to match the required increment of {{step}} {{uom}}',
          {originalValue, correctedValue, step, uom: quantityUom}
        )
      case 'min':
        return t(
          'orderIntake.rmc.steps.shared.quantityCorrection.minAdjustment',
          'Your entered value ({{originalValue}}) was below the minimum of {{minValue}} {{uom}} and has been adjusted',
          {originalValue, minValue, uom: quantityUom}
        )
      case 'max':
        return t(
          'orderIntake.rmc.steps.shared.quantityCorrection.maxAdjustment',
          'Your entered value ({{originalValue}}) was above the maximum of {{maxValue}} {{uom}} and has been adjusted',
          {originalValue, maxValue, uom: quantityUom}
        )
      default:
        return t(
          'orderIntake.rmc.steps.shared.quantityCorrection.defaultAdjustment',
          'Your entered value ({{originalValue}}) was adjusted to {{correctedValue}}',
          {originalValue, correctedValue}
        )
    }
  }, [correction, minValue, maxValue, step, quantityUom, t])

  return {
    correctionMessage,
    hasCorrection: !!correction,
    handleAutoCorrect
  }
}
